import { ORDERER_DUPLICATE_EMAIL_MESSAGE } from "../../common/exceptions";
import {
    HybrisFieldError,
    HybrisFieldValidationResponse,
    ListOption,
} from "../../common/types";
import { isHybrisValidationError } from "../../common/validations";
import { AdministratorInfoData } from "./AdministratorInfoForm";
import {
    Administrator,
    AdministratorInput,
    AdministratorRole,
    AdministratorUpdateInput,
    ChangeAdministratorRoleInput,
} from "./administratorTypes";

export const HYBRIS_ORDERER_ADMIN_UNITS_REQUIRED =
    "An ORDERER_ADMIN must be given at least one unit!";
export const HYBRIS_UNITS_MUST_BELONG_TO_MUNICIPALITY =
    "All units provided must belong to the municipality!";
export const HYBRIS_INVALID_MUNICIPALITY_ROLE =
    "Municipality role must be either ADMIN or ORDERER_ADMIN!";

export const ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE =
    "En Beställare administratör måste tilldelas minst en enhet.";
export const UNITS_MUST_BELONG_TO_MUNICIPALITY_MESSAGE =
    "Alla angivna enheter måste tillhöra kommunen.";
export const INVALID_MUNICIPALITY_ROLE_MESSAGE =
    "Rollen måste vara Administratör eller Beställare administratör.";

export const ADMIN_ROLE_DESCRIPTION =
    "Kan skapa och hantera slutkunder, beställare, administratörer och enheter.";

export const ORDERER_ADMIN_ROLE_DESCRIPTION =
    "Kan skapa nya beställare och slutkunder för de enheter som personen är kopplad till.";

export const ORDERER_ROLE_DESCRIPTION =
    "Kan skapa och hantera slutkunder för de enheter som personen är kopplad till.";

export const ADMINISTRATOR_DUPLICATE_EMAIL_FIELD_MESSAGE =
    "E-postadressen finns redan registrerad";
export const ADMINISTRATOR_DUPLICATE_EMAIL_MODAL_MESSAGE =
    "En användare med den e-postadressen du uppgav existerar redan.";

export const HYBRIS_INVALID_DEMOTE_ROLE = "Municipality role must be ORDERER!";
export const INVALID_DEMOTE_ROLE_MESSAGE = "Rollen måste vara Beställare.";

export const CHANGE_ROLE_FORBIDDEN_SUBTITLE = "Du har inte behörighet att ändra roll.";
export const CHANGE_ROLE_NOT_FOUND_SUBTITLE =
    "Personen kunde inte hittas, eller rollbytet stöds inte i den här miljön.";

export const convertAdministratorFromOutput = (data?: Administrator) =>
    ({
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        phone: data?.municipalPhoneNumber || "",
        email: data?.municipalEmail || "",
        customerCreationAllowed: data?.customerCreationAllowed || false,
        municipalityRole: data?.municipalityRole || "ADMIN",
    }) as AdministratorInfoData;

export const convertAdministratorForCreate = (
    formData: AdministratorInfoData,
    units: ListOption[],
): AdministratorInput => {
    const person = {
        firstName: formData.firstName || "",
        lastName: formData.lastName || "",
        municipalityPhone: formData.phone || "",
        email: formData.email || "",
    };

    if (formData.municipalityRole === "ORDERER_ADMIN") {
        return {
            ...person,
            municipalityRole: "ORDERER_ADMIN",
            unitIds: units.map((unit) => unit.id),
            customerCreationAllowed: formData.customerCreationAllowed,
        };
    }

    return {
        ...person,
        municipalityRole: "ADMIN",
    };
};

export const convertAdministratorForUpdate = (
    formData: AdministratorInfoData,
    units: ListOption[],
    storedRole: AdministratorRole,
): AdministratorUpdateInput => {
    const person = {
        firstName: formData.firstName || "",
        lastName: formData.lastName || "",
        municipalityPhone: formData.phone || "",
        email: formData.email || "",
    };

    if (storedRole === "ORDERER_ADMIN") {
        return {
            ...person,
            unitIds: units.map((unit) => unit.id),
            customerCreationAllowed: formData.customerCreationAllowed,
        };
    }

    return person;
};

/** Demote payload: municipalityRole only — omit unitIds so Hybris keeps units. */
export const convertAdministratorForDemote = (): ChangeAdministratorRoleInput => ({
    municipalityRole: "ORDERER",
});

export const mapHybrisAdministratorErrorField = (
    args: HybrisFieldError,
): { fieldName: string; message: string } => {
    if (args.message === ORDERER_DUPLICATE_EMAIL_MESSAGE) {
        return {
            fieldName: "email",
            message: ADMINISTRATOR_DUPLICATE_EMAIL_FIELD_MESSAGE,
        };
    }

    if (args.message === HYBRIS_ORDERER_ADMIN_UNITS_REQUIRED) {
        return {
            fieldName: "units",
            message: ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE,
        };
    }

    if (args.message === HYBRIS_UNITS_MUST_BELONG_TO_MUNICIPALITY) {
        return {
            fieldName: "units",
            message: UNITS_MUST_BELONG_TO_MUNICIPALITY_MESSAGE,
        };
    }

    if (args.message === HYBRIS_INVALID_MUNICIPALITY_ROLE) {
        return {
            fieldName: "municipalityRole",
            message: INVALID_MUNICIPALITY_ROLE_MESSAGE,
        };
    }

    if (args.message === HYBRIS_INVALID_DEMOTE_ROLE) {
        return {
            fieldName: "municipalityRole",
            message: INVALID_DEMOTE_ROLE_MESSAGE,
        };
    }

    return { fieldName: args.subject, message: "" };
};

const getHttpStatusFromError = (error: unknown): number | undefined => {
    if (error instanceof Response) {
        return error.status;
    }

    if (
        error &&
        typeof error === "object" &&
        "httpStatus" in error &&
        typeof (error as { httpStatus?: unknown }).httpStatus === "number"
    ) {
        return (error as { httpStatus: number }).httpStatus;
    }

    return undefined;
};

export const getChangeAdministratorRoleErrorSubtitle = (error: unknown) => {
    if (isHybrisValidationError(error)) {
        return getModalAdministratorErrorText(error);
    }

    const httpStatus = getHttpStatusFromError(error);

    if (httpStatus === 401 || httpStatus === 403) {
        return CHANGE_ROLE_FORBIDDEN_SUBTITLE;
    }

    if (httpStatus === 404) {
        return CHANGE_ROLE_NOT_FOUND_SUBTITLE;
    }

    return undefined;
};

export const getModalAdministratorErrorText = (response: HybrisFieldValidationResponse) => {
    if (!response?.errors) {
        return undefined;
    }

    if (response.errors.find((error) => error.message === ORDERER_DUPLICATE_EMAIL_MESSAGE)) {
        return ADMINISTRATOR_DUPLICATE_EMAIL_MODAL_MESSAGE;
    }

    const knownError = response.errors.find(
        (error) =>
            error.message === HYBRIS_ORDERER_ADMIN_UNITS_REQUIRED ||
            error.message === HYBRIS_UNITS_MUST_BELONG_TO_MUNICIPALITY ||
            error.message === HYBRIS_INVALID_MUNICIPALITY_ROLE ||
            error.message === HYBRIS_INVALID_DEMOTE_ROLE,
    );

    if (!knownError) {
        return undefined;
    }

    return mapHybrisAdministratorErrorField(knownError).message;
};
